import { useState, useEffect, useMemo } from "react"
import { Button } from "@/modules/shared/ui/components/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/modules/shared/ui/components/dialog"
import { Input } from "@/modules/shared/ui/components/input"
import { Label } from "@/modules/shared/ui/components/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/modules/shared/ui/components/select"
import { UserSummary, OrgRole } from "../../dtos/User"
import { GetUserInfoByEmailAsync } from "@/modules/auth/infrastructure/auth-service"
import { Spinner } from "@/modules/shared/ui/components/spinner"
import { Alert, AlertDescription } from "@/modules/shared/ui/components/alert"
import { InfoIcon } from "lucide-react"

const ROLE_LABELS: Record<string, string> = {
  OrganizationOwner: "Organization Owner",
  TeamMember: "Team Member",
  RegisteredUser: "Registered User",
  SuperAdmin: "Super Admin",
  Admin: "Admin",
  Employee: "Employee",
};

interface AddEditUserDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  user: UserSummary | null
  isSubmitting: boolean
  roles: OrgRole[]
  onSubmit: (data: {
    email: string
    firstName: string
    lastName: string
    phoneNumber?: string
    roleId?: string
  }) => void
}

export function AddEditUserDialog({
  open,
  onOpenChange,
  user,
  isSubmitting,
  roles,
  onSubmit,
}: AddEditUserDialogProps) {
  const invitableRoles = useMemo(
    () => roles.filter((role) => role.name === "TeamMember" || role.name === "OrganizationOwner"),
    [roles]
  )
  const [email, setEmail] = useState("")
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [phoneNumber, setPhoneNumber] = useState("")
  const [roleId, setRoleId] = useState<string>("")
  const [isCheckingEmail, setIsCheckingEmail] = useState(false)
  const [isExistingUser, setIsExistingUser] = useState(false)
  const [lockedFields, setLockedFields] = useState({
    firstName: false,
    lastName: false,
    phoneNumber: false
  })
  const [errors, setErrors] = useState<Record<string, string>>({})

  const isEdit = !!user

  useEffect(() => {
    if (user) {
      setEmail(user.email)
      setFirstName(user.firstName)
      setLastName(user.lastName)
      setPhoneNumber(user.phoneNumber || "")
      setRoleId(user.roleId || "")
      setIsExistingUser(true)
      setLockedFields({
        firstName: !!user.firstName,
        lastName: !!user.lastName,
        phoneNumber: !!user.phoneNumber
      })
    } else {
      setEmail("")
      setFirstName("")
      setLastName("")
      setPhoneNumber("")
      const defaultRole = invitableRoles.find(r => r.name === "TeamMember") || invitableRoles[0];
      setRoleId(defaultRole ? defaultRole.id : "")
      setIsExistingUser(false)
      setLockedFields({
        firstName: false,
        lastName: false,
        phoneNumber: false
      })
    }
  }, [user, open, invitableRoles])

  useEffect(() => {
    if (isEdit || !email || !email.includes("@") || !email.includes(".")) {
      if (!isEdit) {
        setIsExistingUser(false);
      }
      return;
    }

    const timer = setTimeout(async () => {
      setIsCheckingEmail(true);
      try {
        const userInfo = await GetUserInfoByEmailAsync(email);
        if (userInfo) {
          setFirstName(userInfo.firstName || "");
          setLastName(userInfo.lastName || "");
          setPhoneNumber(userInfo.phoneNumber || "");
          setIsExistingUser(true);
          setLockedFields({
            firstName: !!userInfo.firstName,
            lastName: !!userInfo.lastName,
            phoneNumber: !!userInfo.phoneNumber
          });
        } else {
          setIsExistingUser(false);
          setFirstName("");
          setLastName("");
          setPhoneNumber("");
          setLockedFields({
            firstName: false,
            lastName: false,
            phoneNumber: false
          });
        }
      } catch (error) {
        console.error("Failed to check user email", error);
      } finally {
        setIsCheckingEmail(false);
      }
    }, 800);

    return () => clearTimeout(timer);
  }, [email, isEdit]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!isEdit && !email) newErrors.email = "Email is required.";
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) newErrors.email = "Invalid email address.";
    if (!roleId) newErrors.roleId = "Role is required.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    onSubmit({ email, firstName, lastName, phoneNumber, roleId: roleId || undefined });
  }

  const formatRoleName = (name: string) => {
    return ROLE_LABELS[name] || name.replace(/([A-Z])/g, " $1").trim();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px] bg-card">
        <form onSubmit={handleSubmit} noValidate>
          <DialogHeader>
            <DialogTitle>{isEdit ? "Edit User" : "Add User"}</DialogTitle>
            <DialogDescription>
              {isEdit
                ? "Update the user's information and role."
                : "Invite a new member to your organization."}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (e.target.value) {
                    setErrors(prev => {
                      const { email: _email, ...rest } = prev;
                      return rest;
                    });
                  }
                }}
                placeholder="email@example.com"
                disabled={isSubmitting || isEdit}
                className="disabled:bg-muted"
              />
              {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
              {isEdit && (
                <p className="text-xs text-muted-foreground">Email cannot be changed after registration.</p>
              )}
            </div>

            {isCheckingEmail ? (
              <div className="flex flex-col items-center justify-center py-8 gap-3">
                <Spinner className="size-8 text-primary" />
                <p className="text-sm text-muted-foreground">Checking user information...</p>
              </div>
            ) : (
              <>
                {isExistingUser && !isEdit && (
                  <Alert className="bg-primary/5 border-primary/20">
                    <InfoIcon className="size-4 text-primary" />
                    <AlertDescription className="text-primary text-xs">
                      This user already has a platform account. Some details are locked.
                    </AlertDescription>
                  </Alert>
                )}
                <div className="grid gap-2">
                  <Label htmlFor="firstName">First Name</Label>
                  <Input
                    id="firstName"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="First Name"
                    disabled={isSubmitting || lockedFields.firstName}
                    className={lockedFields.firstName ? "disabled:bg-muted" : ""}
                  />
                  {errors.firstName && <p className="text-sm text-destructive">{errors.firstName}</p>}
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="lastName">Last Name</Label>
                  <Input
                    id="lastName"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Last Name"
                    disabled={isSubmitting || lockedFields.lastName}
                    className={lockedFields.lastName ? "disabled:bg-muted" : ""}
                  />
                  {errors.lastName && <p className="text-sm text-destructive">{errors.lastName}</p>}
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="phoneNumber">Phone Number</Label>
                  <Input
                    id="phoneNumber"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="+1 (555) 000-0000"
                    disabled={isSubmitting || lockedFields.phoneNumber}
                    className={lockedFields.phoneNumber ? "disabled:bg-muted" : ""}
                  />
                </div>
              </>
            )}
            {invitableRoles.length > 0 && (
              <div className="grid gap-2">
                <Label htmlFor="role">Role</Label>
                <Select
                  value={roleId}
                  onValueChange={setRoleId}
                  disabled={isSubmitting}
                >
                  <SelectTrigger id="role" className="bg-card">
                    <SelectValue placeholder="Select a role" />
                  </SelectTrigger>
                  <SelectContent className="bg-card">
                    {invitableRoles.map((role) => (
                      <SelectItem key={role.id} value={role.id}>
                        {formatRoleName(role.name)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.roleId && <p className="text-sm text-destructive">{errors.roleId}</p>}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting} className="bg-primary hover:bg-primary/90 text-primary-foreground">
              {isSubmitting ? "Saving..." : isEdit ? "Save Changes" : "Add User"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
